import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { updateTicket, deleteTicket, deleteAllTickets, markActivityRead, TICKET_STATES, TICKET_PRIORITIES } from '../../services/workspaceService';
import { getChecksByClaimRecordId } from '../../services/claimService';
import ConfirmModal from '../common/ConfirmModal';
import DocusignAssign from './DocusignAssign';
import { draftKey, readDraft, writeDraft, removeDraft } from '../../utils/drafts';
import './TeamWorkspace.css';

export const stateLabel = (state, es) => ({ pending: es ? 'Pendiente' : 'Pending', in_progress: es ? 'En curso' : 'In progress', waiting: es ? 'En espera' : 'Waiting', resolved: es ? 'Resuelto' : 'Resolved' }[state] || state);
export const priorityLabel = (priority, es) => ({ high: es ? 'Alta' : 'High', medium: es ? 'Media' : 'Medium', low: es ? 'Baja' : 'Low' }[priority] || priority);
const dateLabel = (value, es) => {
  const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString(es ? 'es' : 'en-US', { dateStyle: 'medium', timeStyle: 'short' }) : (es ? 'Guardando...' : 'Saving...');
};

const NOTES_PAGE_SIZE = 25;
// Borradores de seguimiento (comentario + estado) por caso y por usuario
// (sessionStorage, ver utils/drafts): sobreviven a abrir otro caso, cambiar de
// sección o recargar, y se borran al cerrar sesión.
const loadDrafts = key => readDraft(key) || {};
const includesTerm = (values, term) => !term || values.filter(Boolean).join(' ').toLocaleLowerCase().includes(term);

// Rediseno 2026-09-21 (pedido explicito del cliente). Dos rondas:
// 1. Se saco el formulario "+ Nuevo caso o nota" -- crear vive en
//    ClaimCases.jsx (ClaimDetailPage.jsx), no aca.
// 2. Se saco el layout de dos columnas (lista + panel a la derecha) --
//    pedido explicito: "no quiero que se despliegue a la derecha, por lo
//    menos hacia abajo completo". Ahora cada tarjeta se expande in-place,
//    hacia abajo, a todo el ancho -- mismo patron que ya usa el resto del
//    panel admin (ClaimsDirectory.jsx, tarjetas de reclamo expandibles).
export default function TeamWorkspace({ activity = false, tickets, events, claimNotes = [], user, language, error, loading, onRetry }) {
  const es = language === 'es';
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(false);
  // Vista dentro de "Casos y notas": los dos listados comparten el buscador y
  // cada pestaña muestra cuántos resultados tiene.
  const [view, setView] = useState('cases');
  const [notePage, setNotePage] = useState(1);
  const draftsKey = draftKey('tickets', user?.uid);
  const [drafts, setDrafts] = useState(() => loadDrafts(draftsKey));
  useEffect(() => {
    if (Object.keys(drafts).length === 0) removeDraft(draftsKey); else writeDraft(draftsKey, drafts);
  }, [drafts, draftsKey]);
  const setDraft = (id, patch) => setDrafts(current => ({ ...current, [id]: { ...current[id], ...patch } }));
  const clearDraft = id => setDrafts(current => { const { [id]: removed, ...rest } = current; return rest; });
  const isDirty = item => { const d = drafts[item.id]; return !!d && (!!(d.comment || '').trim() || (d.status !== undefined && d.status !== item.status)); };
  const [pendingClearAll, setPendingClearAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deletingTicket, setDeletingTicket] = useState(false);
  const [openingClaim, setOpeningClaim] = useState(null);
  const term = query.trim().toLocaleLowerCase();
  const filteredTickets = tickets.filter(item =>
    (filter === 'all' || item.status === filter) && includesTerm([item.title, item.reference, item.createdBy, item.text], term)
  );
  // La búsqueda cubre TODAS las notas; solo se recorta lo que se dibuja.
  const filteredNotes = useMemo(() => claimNotes.filter(note =>
    includesTerm([note.text, note.claimNumber, note.clientName, note.createdByName, note.createdByEmail], term)
  ), [claimNotes, term]);
  const notePages = Math.max(1, Math.ceil(filteredNotes.length / NOTES_PAGE_SIZE));
  const currentNotePage = Math.min(notePage, notePages);
  const noteFrom = filteredNotes.length === 0 ? 0 : (currentNotePage - 1) * NOTES_PAGE_SIZE + 1;
  const noteTo = Math.min(filteredNotes.length, currentNotePage * NOTES_PAGE_SIZE);
  const visibleNotes = filteredNotes.slice(noteFrom - 1, noteTo);
  const changeQuery = value => { setQuery(value); setNotePage(1); };

  // Abrir otro caso NO descarta lo escrito: cada caso conserva su borrador.
  const toggleExpand = id => setExpandedId(current => current === id ? null : id);

  // "Ir al reclamo" -- mismo patron que UpcomingMeetingsWidget.jsx: busca
  // los cheques del reclamo para armar la URL con ?checks=, y si todavia no
  // tiene ninguno (falta el Paso 2) abre igual la vista de "sin cheques"
  // que ya soporta ClaimDetailPage.jsx.
  const handleOpenTicketClaim = async claimRecordId => {
    setOpeningClaim(claimRecordId);
    try {
      const checks = await getChecksByClaimRecordId(claimRecordId);
      const url = checks.length > 0
        ? `/claim-status/claim/${claimRecordId}?checks=${checks.map(c => c.id).join(',')}`
        : `/claim-status/claim/${claimRecordId}`;
      window.open(url, '_blank');
    } catch {
      toast.error(es ? 'No se pudo abrir el reclamo.' : 'Could not open the claim.');
    } finally {
      setOpeningClaim(null);
    }
  };

  const perform = async fn => {
    setBusy(true);
    try { await fn(); } catch (err) {
      toast.error(err.message === 'HISTORY_FULL'
        ? (es ? 'Este caso llegó a 100 actualizaciones. Pídele al ajustador que cree uno nuevo desde el reclamo.' : 'This case reached 100 updates. Ask the adjuster to create a new one from the claim.')
        : (es ? 'No se pudo guardar. Intenta de nuevo.' : 'Could not save. Please try again.'));
    } finally { setBusy(false); }
  };

  // "Borrar todo" -- pedido explicito 2026-09-21, admin-only, irreversible.
  // Solo borra tickets (kind: 'ticket'), no toca Actividad ni el respaldo
  // de notas de reclamo (ver deleteAllTickets en workspaceService.js).
  const handleClearAll = async () => {
    setClearingAll(true);
    try {
      const result = await deleteAllTickets();
      setExpandedId(null);
      setDrafts({}); // los casos ya no existen: sus borradores tampoco
      setPendingClearAll(false);
      toast.success(es ? `${result.deletedCount} caso(s) eliminados.` : `${result.deletedCount} case(s) deleted.`);
    } catch (err) {
      toast.error(es ? 'No se pudo borrar. Intenta de nuevo.' : 'Could not delete. Please try again.');
    } finally {
      setClearingAll(false);
    }
  };

  // Borrar UN caso puntual — pedido explícito 2026-09-22 ("como borro un
  // case creado antes"). Admin-only (firestore.rules), gateado por
  // ConfirmModal igual que "Borrar todo".
  const handleDeleteTicket = async () => {
    if (!pendingDeleteId) return;
    setDeletingTicket(true);
    try {
      await deleteTicket(pendingDeleteId);
      if (expandedId === pendingDeleteId) setExpandedId(null);
      clearDraft(pendingDeleteId);
      setPendingDeleteId(null);
      toast.success(es ? 'Caso eliminado.' : 'Case deleted.');
    } catch (err) {
      toast.error(es ? 'No se pudo eliminar. Intenta de nuevo.' : 'Could not delete. Please try again.');
    } finally {
      setDeletingTicket(false);
    }
  };

  const eventTitle = item => ({
    claim_created: es ? 'Nuevo reclamo registrado' : 'New claim created',
    claim_updated: es ? 'Reclamo actualizado' : 'Claim updated',
    payment_full: es ? 'Comisión confirmada por el operador' : 'Commission confirmed by operator',
    payment_partial: es ? 'Pago parcial registrado' : 'Partial payment recorded',
    statement_created: es ? 'Closing statement creado' : 'Closing statement created',
    statement_status: es ? 'Closing statement actualizado' : 'Closing statement updated',
    ticket_status: es ? 'Estado de caso actualizado' : 'Case status updated',
    docusign_signed: es ? 'Documento firmado en DocuSign' : 'Document signed in DocuSign',
    docusign_declined: es ? 'Documento declinado en DocuSign' : 'Document declined in DocuSign',
    docusign_voided: es ? 'Sobre anulado en DocuSign' : 'Envelope voided in DocuSign',
    claim_step: item.awaitingPa
      ? (es ? 'Decisión del PA pendiente' : 'PA decision pending')
      : (es ? 'Reclamo cambió de paso' : 'Claim moved to a new step')
  }[item.event] || (es ? 'Actividad registrada' : 'Activity recorded'));

  if (error) {
    return (
      <section className="team-workspace team-message" role="alert">
        <p>{es ? 'No se pudieron cargar los casos y la actividad. Revisa la conexión y el acceso de administrador.' : 'Could not load cases and activity. Check your connection and administrator access.'}</p>
        <button className="team-btn-primary" onClick={onRetry}>{es ? 'Reintentar' : 'Retry'}</button>
      </section>
    );
  }
  if (loading) return <p role="status">{es ? 'Cargando actividad del equipo...' : 'Loading team activity...'}</p>;

  return (
    <section className={`team-workspace ${activity ? '' : 'team-cases'}`}>
      <header className="team-heading">
        <div>
          {/* El título de la página ya lo pone el Dashboard; aquí solo queda para lectores de pantalla. */}
          <h3 className="sr-only">{activity ? (es ? 'Actividad del equipo' : 'Team activity') : (es ? 'Casos y notas' : 'Cases and notes')}</h3>
          <p>
            {activity
              ? (es ? 'Lo que ocurre en tus reclamos, registrado automáticamente.' : 'What happens in your claims, logged automatically.')
              : (es ? 'Revisa las solicitudes de los ajustadores y da seguimiento a cada reclamo.' : 'Review adjuster requests and follow up on each claim.')}
          </p>
        </div>
        {!activity && tickets.length > 0 && (
          <button className="team-btn-ghost" onClick={() => setPendingClearAll(true)} disabled={busy || clearingAll}>{es ? 'Borrar todo' : 'Clear all'}</button>
        )}
      </header>

      {!activity && (
        <ConfirmModal
          open={pendingClearAll}
          icon="fa-trash"
          danger
          title={es ? 'Borrar todos los casos' : 'Delete all cases'}
          message={es
            ? `¿Eliminar los ${tickets.length} caso(s) de Casos y notas? Esto no borra la Actividad ni el respaldo de notas de reclamo. No se puede deshacer.`
            : `Delete all ${tickets.length} case(s) in Cases and notes? This does not touch Activity or the claim notes backup. This cannot be undone.`}
          confirmLabel={clearingAll ? (es ? 'Borrando...' : 'Deleting...') : (es ? 'Borrar todo' : 'Delete all')}
          cancelLabel={es ? 'Cancelar' : 'Cancel'}
          loading={clearingAll}
          onConfirm={handleClearAll}
          onCancel={() => setPendingClearAll(false)}
        />
      )}

      {!activity && (
        <ConfirmModal
          open={!!pendingDeleteId}
          icon="fa-trash"
          danger
          title={es ? 'Eliminar caso' : 'Delete case'}
          message={es ? '¿Eliminar este caso? No se puede deshacer.' : 'Delete this case? This cannot be undone.'}
          confirmLabel={deletingTicket ? (es ? 'Eliminando...' : 'Deleting...') : (es ? 'Eliminar' : 'Delete')}
          cancelLabel={es ? 'Cancelar' : 'Cancel'}
          loading={deletingTicket}
          onConfirm={handleDeleteTicket}
          onCancel={() => setPendingDeleteId(null)}
        />
      )}

      {activity ? (
        <div className="team-feed">
          <div className="team-feed-header">
            <h3>{es ? 'Últimos movimientos' : 'Latest activity'}</h3>
            <button className="team-link-btn" disabled={busy || !events.some(item => !(item.readBy || []).includes(user.uid))} onClick={() => perform(() => markActivityRead(events))}>{es ? 'Marcar todo leído' : 'Mark all read'}</button>
          </div>
          {events.length === 0 && <p className="team-message">{es ? 'Las nuevas acciones aparecerán aquí automáticamente. No se genera actividad retroactiva.' : 'New actions will appear here automatically. Past actions are not backfilled.'}</p>}
          {events.map(item => (
            <article key={item.id} className={`team-event ${!(item.readBy || []).includes(user.uid) ? 'unread' : ''}`}>
              <span className={`team-event-icon ${item.event?.startsWith('payment') ? 'green' : 'blue'}`}><i aria-hidden="true" className={`fa-solid ${item.event?.startsWith('payment') ? 'fa-circle-check' : 'fa-clock-rotate-left'}`} /></span>
              <div>
                <h3>{eventTitle(item)}</h3>
                <p>{item.reference}{typeof item.amount === 'number' && ` - ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.amount)}`}{item.status && ` - ${stateLabel(item.status, es)}`}</p>
                {item.event === 'docusign_signed' && <DocusignAssign item={item} es={es} busy={busy} perform={perform} />}
                <small>{item.createdBy} - {dateLabel(item.createdAt, es)}</small>
                <span className="team-auto">{es ? 'Registro automatico' : 'Automatic entry'}</span>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <>
          <div className="team-case-toolbar">
            <label className="team-search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              <input type="search" value={query} onChange={event => changeQuery(event.target.value)} aria-label={es ? 'Buscar casos y notas' : 'Search cases and notes'} placeholder={es ? 'Buscar en casos y notas: texto, reclamo, cliente o autor…' : 'Search cases and notes: text, claim, client or author…'} />
            </label>
            <span className="team-result-count" role="status">
              {view === 'cases'
                ? `${filteredTickets.length} ${es ? (filteredTickets.length === 1 ? 'caso' : 'casos') : (filteredTickets.length === 1 ? 'case' : 'cases')}`
                : (filteredNotes.length > NOTES_PAGE_SIZE
                  ? (es ? `Mostrando ${noteFrom}–${noteTo} de ${filteredNotes.length} notas` : `Showing ${noteFrom}–${noteTo} of ${filteredNotes.length} notes`)
                  : `${filteredNotes.length} ${es ? (filteredNotes.length === 1 ? 'nota' : 'notas') : (filteredNotes.length === 1 ? 'note' : 'notes')}`)}
            </span>
          </div>
          <div className="cd-view-toggle team-views" role="group" aria-label={es ? 'Tipo de resultado' : 'Result type'}>
            <button type="button" className={`cd-view-btn ${view === 'cases' ? 'active' : ''}`} aria-pressed={view === 'cases'} onClick={() => setView('cases')}>
              {es ? 'Casos' : 'Cases'} <span className="team-filter-count">{filteredTickets.length}</span>
            </button>
            <button type="button" className={`cd-view-btn ${view === 'notes' ? 'active' : ''}`} aria-pressed={view === 'notes'} onClick={() => setView('notes')}>
              {es ? 'Notas de reclamos' : 'Claim notes'} <span className="team-filter-count">{filteredNotes.length}</span>
            </button>
          </div>
          {view === 'cases' && <div className="team-filters team-status-filters" role="group" aria-label={es ? 'Filtrar por estado' : 'Filter by status'}>
            <span className="team-filter-label">{es ? 'Estado' : 'Status'}</span>
            {['all', ...TICKET_STATES].map(state => (
              <button key={state} className="team-chip" aria-pressed={filter === state} onClick={() => setFilter(state)}>
                {state === 'all' ? (es ? 'Todos' : 'All') : stateLabel(state, es)}
                <span className="team-filter-count">{state === 'all' ? tickets.length : tickets.filter(item => item.status === state).length}</span>
              </button>
            ))}
          </div>}

          {view === 'cases' && <div className="team-list team-list-stacked">
            {filteredTickets.map(item => {
              const isOpen = expandedId === item.id;
              const draft = drafts[item.id] || {};
              return (
                <div key={item.id} className={`team-ticket-card ${isOpen ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="team-ticket-summary"
                    aria-expanded={isOpen}
                    aria-controls={`ticket-${item.id}`}
                    disabled={busy}
                    onClick={() => toggleExpand(item.id)}
                  >

                    <span className="team-ticket-summary-body">
                      <span className="team-ticket-meta">
                        {item.type === 'note' ? (es ? 'Nota' : 'Note') : (es ? 'Caso' : 'Case')}
                        <span className="team-ticket-badges">
                          <span className={`team-priority team-priority-${item.priority || 'medium'}`}>{priorityLabel(item.priority || 'medium', es)}</span>
                          <span className={`team-status ${item.status}`}>{stateLabel(item.status, es)}</span>
                          {isDirty(item) && <span className="team-draft-badge">{es ? 'Borrador sin guardar' : 'Unsaved draft'}</span>}
                        </span>
                      </span>
                      <strong>{item.title}</strong>
                      <span className="team-ticket-reference">{item.reference}</span>
                      <small>{item.createdBy} - {dateLabel(item.createdAt, es)}</small>
                    </span>
                    <i className={`fa-solid fa-chevron-down team-ticket-chevron ${isOpen ? 'open' : ''}`} aria-hidden="true"></i>
                  </button>

                  {isOpen && (
                    <div className="team-ticket-expanded" id={`ticket-${item.id}`}>
                      {(item.history || []).length > 0 && (
                        <details className="team-history">
                          <summary>{es ? 'Historial de seguimiento' : 'Follow-up history'} ({(item.history || []).length})</summary>
                          {[...(item.history || [])].reverse().map((entry, index) => (
                            // Las respuestas escritas se destacan para leerlas fácil (reunión 22-sep).
                            <div key={index} className={entry.text ? 'team-reply' : undefined}>
                              <p className="team-body">{entry.text || (entry.priorityTo
                                ? `${es ? 'Prioridad' : 'Priority'}: ${priorityLabel(entry.priorityFrom || 'medium', es)} -> ${priorityLabel(entry.priorityTo, es)}`
                                : `${stateLabel(entry.from, es)} -> ${stateLabel(entry.to, es)}`)}</p>
                              {entry.text && entry.to && <p className="team-history-state"><span className={`team-status ${entry.to}`}>{stateLabel(entry.to, es)}</span></p>}
                              <small>{entry.createdBy} - {dateLabel(entry.createdAt, es)}</small>
                            </div>
                          ))}
                        </details>
                      )}
                      {(item.history || []).length === 0 && (
                        <p className="team-history-empty">{es ? 'Sin actualizaciones todavía. Este es el mensaje original del caso.' : 'No updates yet. This is the original case message.'}</p>
                      )}
                      <h3 className="team-original-title">{es ? 'Mensaje original' : 'Original message'}</h3>
                      <p className="team-body">{item.text}</p>

                      <div className="team-case-actions">
                        {item.claimRecordId && (
                          <button type="button" className="team-btn-ghost team-go-to-claim" disabled={openingClaim === item.claimRecordId} onClick={() => handleOpenTicketClaim(item.claimRecordId)}>
                            <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i> {openingClaim === item.claimRecordId ? (es ? 'Abriendo...' : 'Opening...') : (es ? 'Ir al reclamo' : 'Go to claim')}
                          </button>
                        )}
                        <button type="button" className="team-btn-ghost team-delete-case" onClick={() => setPendingDeleteId(item.id)}>
                          <i className="fa-solid fa-trash" aria-hidden="true"></i> {es ? 'Eliminar caso' : 'Delete case'}
                        </button>
                      </div>

                      <div className="team-detail-controls">
                        <label>
                          {es ? 'Prioridad' : 'Priority'}
                          <select value={item.priority || 'medium'} disabled={busy} onChange={event => perform(async () => { await updateTicket(item.id, { priority: event.target.value }); toast.success(es ? 'Prioridad actualizada.' : 'Priority updated.'); })}>
                            {TICKET_PRIORITIES.map(priority => <option key={priority} value={priority}>{priorityLabel(priority, es)}</option>)}
                          </select>
                          <small className="team-save-hint">{es ? 'Se guarda al cambiarla.' : 'Saved as soon as you change it.'}</small>
                        </label>
                      </div>

                      <form onSubmit={event => { event.preventDefault(); perform(async () => { await updateTicket(item.id, { comment: draft.comment || '', status: draft.status ?? item.status }); clearDraft(item.id); toast.success(es ? 'Seguimiento guardado.' : 'Follow-up saved.'); }); }}>
                        <label>{es ? 'Añadir seguimiento' : 'Add follow-up'}<textarea disabled={busy} placeholder={es ? 'Escribe una actualización sobre este caso…' : 'Write an update on this case…'} required maxLength={4000} value={draft.comment || ''} onChange={event => setDraft(item.id, { comment: event.target.value })} /></label>
                        <label>{es ? 'Estado del caso' : 'Case status'}
                          <select value={draft.status ?? item.status} disabled={busy} onChange={event => setDraft(item.id, { status: event.target.value })}>
                            {TICKET_STATES.map(state => <option key={state} value={state}>{stateLabel(state, es)}</option>)}
                          </select>
                        </label>
                        <p className="team-save-hint" role="status">
                          {isDirty(item)
                            ? (es ? 'Borrador sin guardar: el comentario y el estado se guardan juntos con «Guardar seguimiento». Lo escrito se conserva si abres otro caso.' : 'Unsaved draft: the comment and status are saved together with “Save follow-up”. What you typed is kept if you open another case.')
                            : (es ? 'El comentario y el estado se guardan juntos con «Guardar seguimiento».' : 'The comment and status are saved together with “Save follow-up”.')}
                        </p>
                        <div className="team-case-actions">
                          <button className="team-btn-primary" disabled={busy || !(draft.comment || '').trim()}>{busy ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar seguimiento' : 'Save follow-up')}</button>
                          {isDirty(item) && <button type="button" className="team-btn-ghost" disabled={busy} onClick={() => clearDraft(item.id)}>{es ? 'Descartar borrador' : 'Discard draft'}</button>}
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredTickets.length === 0 && (
              <div className="team-message team-empty">
                <i className="fa-regular fa-folder-open" aria-hidden="true" />
                <h3>{es ? 'No hay casos para mostrar' : 'No cases to show'}</h3>
                <p>{query || filter !== 'all' ? (es ? 'Prueba otra búsqueda o cambia el filtro de estado.' : 'Try another search or change the status filter.') : (es ? 'Los casos aparecerán aquí cuando se creen desde un reclamo.' : 'Cases will appear here when created from a claim.')}</p>
              </div>
            )}
          </div>}

          {view === 'cases' && <p className="team-create-hint">
            <i className="fa-solid fa-circle-info" aria-hidden="true"></i>
            {es ? ' Los casos y notas nuevos se crean desde la ficha del reclamo, no desde aquí.' : ' New cases and notes are created from the claim page, not from here.'}
          </p>}

          {/* Respaldo de notas de reclamos (ClaimNotes.jsx) -- pedido
              explicito 2026-09-20: si un P.A. deja una nota en un reclamo,
              debe verse aca para todos los admin, sin tener que entrar al
              reclamo. Solo lectura -- editar/eliminar sigue viviendo en la
              propia pagina del reclamo. */}
          {view === 'notes' && (
          <div className="team-feed team-notes-backup">
            <div className="team-feed-header">
              <h3>{es ? 'Notas de reclamos (respaldo)' : 'Claim notes (backup)'}</h3>
            </div>
            {claimNotes.length === 0 && <p className="team-message">{es ? 'Todavía no hay notas de reclamos.' : 'No claim notes yet.'}</p>}
            {claimNotes.length > 0 && filteredNotes.length === 0 && <p className="team-message">{es ? 'Ninguna nota coincide con la búsqueda.' : 'No notes match the search.'}</p>}
            {visibleNotes.map(note => (
              <article key={note.id} className="team-event">
                <span className="team-event-icon blue"><i aria-hidden="true" className="fa-solid fa-note-sticky" /></span>
                <div>
                  <h3>{note.claimNumber ? `#${note.claimNumber}${note.clientName ? ` - ${note.clientName}` : ''}` : (note.clientName || (es ? 'Reclamo' : 'Claim'))}</h3>
                  <p>{note.text}</p>
                  <div className="team-note-meta">
                    <small>{note.createdByName || note.createdByEmail}</small>
                    <small>{dateLabel(note.createdAt, es)}</small>
                    {note.claimRecordId && (
                      <Link className="team-link-btn team-note-claim-link" to={`/claim-status/claim/${note.claimRecordId}?section=claim-notes`}>
                        {es ? 'Abrir reclamo' : 'Open claim'} <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                </div>
              </article>
            ))}
            {filteredNotes.length > NOTES_PAGE_SIZE && (
              <nav className="team-pager" aria-label={es ? 'Paginación de notas' : 'Notes pagination'}>
                <button type="button" className="team-btn-ghost" disabled={currentNotePage <= 1} onClick={() => setNotePage(currentNotePage - 1)}>{es ? 'Más recientes' : 'Newer'}</button>
                <span>{es ? `Página ${currentNotePage} de ${notePages}` : `Page ${currentNotePage} of ${notePages}`}</span>
                <button type="button" className="team-btn-ghost" disabled={currentNotePage >= notePages} onClick={() => setNotePage(currentNotePage + 1)}>{es ? 'Más antiguas' : 'Older'}</button>
              </nav>
            )}
          </div>
          )}
        </>
      )}
    </section>
  );
}
